import { styled } from '@rybr/styled-modules'
import { useLocale } from 'next-intl'
import React, { ComponentProps, useMemo } from 'react'
import { useTranslations } from 'use-intl'

import { useIntlPriceFormatter } from '@ntc/hooks/useIntlPriceFormatter'

import { Image as FallbackImage } from '@components/Image'
import { LOCALE } from '@constants/constants.i18n'
import { BADGE, Badge, BadgeProps } from '@constants/constants.product'
import { theme } from '@src/theme'

import styles from './ProductTile.module.scss'

export type ProductTileProps = {
  url: string
  image?: {
    src?: string
    height?: number
    width?: number
  }
  brandName?: string
  description?: string
  listPrice?: number | null
  salePrice?: number | null
  badge?: Badge | null
} & ComponentProps<typeof Tile>

// TODO - convert to using the BADGE_MAP from constants.product.ts
const BADGE_MAP: {
  [K in Badge]: BadgeProps
} = {
  onlineExclusive: {
    id: BADGE.onlineExclusive,
    translationKey: 'global.productTile.badge-onlineExclusive',
    backgroundColor: theme['color-badge-online-exclusive'],
    textColor: theme['color-secondary']
  },
  clearance: {
    id: BADGE.clearance,
    translationKey: 'global.productTile.badge-clearance',
    backgroundColor: theme['color-badge-clearance'],
    textColor: theme['color-accent']
  },
  priceDrop: {
    id: BADGE.priceDrop,
    translationKey: 'global.productTile.badge-priceDrop',
    backgroundColor: theme['color-brand-deal'],
    textColor: theme['color-secondary']
  },
  buyInBulk: {
    id: BADGE.buyInBulk,
    translationKey: 'global.productTile.badge-buyInBulk',
    backgroundColor: theme['color-primary'],
    textColor: theme['color-neutral-1']
  },
  greatBuy: {
    id: BADGE.greatBuy,
    translationKey: 'global.productTile.badge-greatBuy',
    backgroundColor: theme['color-secondary'],
    textColor: theme['color-brand-deal']
  },
  proPick: {
    id: BADGE.proPick,
    translationKey: 'global.productTile.badge-proPick',
    backgroundColor: theme['color-primary'],
    textColor: theme['color-brand-deal']
  },
  eco: {
    id: BADGE.eco,
    translationKey: 'global.productTile.badge-eco',
    backgroundColor: theme['color-badge-eco'],
    textColor: theme['color-neutral-1']
  }
}

const Tile = styled.a(styles.Tile)
const InfoContainer = styled.div(styles.InfoContainer)
const BrandName = styled.p(styles.BrandName)
const Description = styled.p(styles.Description)
const PricesContainer = styled.div(styles.PricesContainer)
const PriceContainer = styled.div(styles.PriceContainer)
const SmallPrice = styled.p(styles.SmallPrice)
const DisplayPrice = styled.p(styles.DisplayPrice)
const OriginalPrice = styled.p(styles.OriginalPrice)
const BadgeContainer = styled.span<{ backgroundColor: string; color: string }>(
  styles.BadgeContainer,
  ({ backgroundColor, color }) => ({
    backgroundColor,
    color
  })
)

const usePrice = <T extends string | number | null>(rawPrice?: T) => {
  const locale = useLocale()

  return useMemo(() => {
    if (rawPrice == null) {
      return {
        dollars: null,
        cents: null
      }
    }

    const [dollars, cents] = typeof rawPrice === 'number' ? rawPrice.toFixed(2).split('.') : rawPrice.split('.')

    return {
      dollars,
      cents: cents == null || cents === '00' ? '' : cents
    }
  }, [locale, rawPrice])
}

export const ProductTile = React.forwardRef<HTMLAnchorElement, ProductTileProps>(
  ({ url, image, brandName, description, listPrice, salePrice, badge, ...tileProps }, forwardedRef) => {
    const t = useTranslations()
    const locale = useLocale()

    const price = usePrice(listPrice === salePrice ? listPrice : salePrice)
    const originalPrice = useIntlPriceFormatter(listPrice)

    return (
      <Tile
        ref={forwardedRef}
        href={url}
        {...tileProps}
      >
        {badge ? (
          <BadgeContainer
            $backgroundColor={BADGE_MAP[badge].backgroundColor}
            $color={BADGE_MAP[badge].textColor}
          >
            {t(BADGE_MAP[badge].translationKey)}
          </BadgeContainer>
        ) : null}
        <FallbackImage
          {...image}
          className={styles.ProductImage}
        />
        <InfoContainer>
          <BrandName>{brandName}</BrandName>
          <Description>{description}</Description>
        </InfoContainer>
        <PricesContainer>
          <PriceContainer>
            {!price.dollars ? null : locale === LOCALE.en ? (
              <>
                <SmallPrice>{'$'}</SmallPrice>
                <DisplayPrice>{price.dollars}</DisplayPrice>
                <SmallPrice>{price.cents}</SmallPrice>
              </>
            ) : (
              <>
                <DisplayPrice>{price.dollars}</DisplayPrice>
                <SmallPrice>{price.cents}</SmallPrice>
                <SmallPrice>{'$'}</SmallPrice>
              </>
            )}
          </PriceContainer>
          {listPrice && salePrice && originalPrice && listPrice !== salePrice ? (
            <OriginalPrice>{originalPrice}</OriginalPrice>
          ) : (
            <>&nbsp;</>
          )}
        </PricesContainer>
      </Tile>
    )
  }
)

ProductTile.displayName = 'ProductTile'
